import type { ReactNode } from 'react';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { CardActions, CardButton, ResultCard, ResultCardHeader } from '../ResultCard';

/** Resolution states juneau reports, mapped to the line the card shows instead of buttons. */
const RESOLUTION_KEYS: Record<string, string> = {
  confirmed: 'proposals.resolution.confirmed',
  cancelled: 'proposals.resolution.cancelled',
  expired: 'proposals.resolution.expired'
};

interface Props {
  title: string;
  description: string;
  proposalId: string;
  /** Set once the user acted — the card then states the outcome instead of asking again. */
  resolved?: string | undefined;
  onConfirm: (proposalId: string) => void;
  onCancel: (proposalId: string) => void;
  /** Holds the confirm back, e.g. until a card's form is filled. */
  confirmDisabled?: boolean;
  children: ReactNode;
}

/** Card surface shared by the bulk proposal cards: header, listed rows, confirm/cancel. */
export function ProposalShell({
  title,
  description,
  proposalId,
  resolved,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  children
}: Props) {
  const t = useFeatureTranslation('aiChat');

  return (
    <ResultCard>
      <ResultCardHeader title={title} />
      <span className='text-foreground-muted text-sm'>{description}</span>

      {children}

      {resolved === undefined ? (
        <CardActions>
          <CardButton tone='outline' onClick={() => onCancel(proposalId)}>
            {t('proposals.cancel')}
          </CardButton>
          <CardButton disabled={confirmDisabled} onClick={() => onConfirm(proposalId)}>
            {t('proposals.confirm')}
          </CardButton>
        </CardActions>
      ) : (
        // A resolution juneau adds later has no key here — showing nothing beats
        // rendering a raw translation key at the user.
        <span className='text-foreground-muted text-sm'>
          {RESOLUTION_KEYS[resolved] === undefined ? null : t(RESOLUTION_KEYS[resolved])}
        </span>
      )}
    </ResultCard>
  );
}
